/* eslint-disable no-unused-vars */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { useLocation, useNavigate } from 'react-router-dom';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import ProfileAddButton from '@/shared-components/buttons/components/ProfileAddButton';
import BoardArtworkPreview from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-board-artwork/BoardArtworkPreview';
import ImageManager from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-picture/ImageManager';
import PollDisplay  from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-polls/PollDisplay';
import PollManager from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-polls/PollManager';
import QuizDisplay from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-quiz/QuizDisplay';
import QuizManager from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-quiz/QuizManager';
import TagPopup from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-tags/add-tags-popup/TagPopup';
import { useTagging } from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-tags/useTagging';
import BoardBodyVideo from '@/shared-components/forms/add-board-content/board-text-body/add-element/add-video/BoardBodyVideo';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import ArticleRecommendations from '../add-article/published-article/ArticleRecommendations';
import { sanitizePostHtml } from '../saveFunctions';

import ArtworkCarousel from './add-element/add-board-artwork/ArtworkCarousel';
import ArtworkDisplay from './add-element/add-board-artwork/ArtworkDisplay';
import ArtworkManager from './add-element/add-board-artwork/ArtworkManager';
import { applyTextCustomization } from './add-element/add-customization/TextCustomization';
import TextFormattingToolbar from './add-element/add-customization/TextFormattingToolbar';
import ImageDisplay from './add-element/add-picture/ImageDisplay';

import './BoardTextBody.css';

export default function BoardTextBody({
  formState,
  setFormState,
  editableRef,
  previewMode = false,
  postMode = false,
  hideAddButton = false,
  isEdit = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profileData } = useContext(ProfileDataContext);
  const currentArticleId = formState._id || null;
  const galleryId = formState.gallery || null;

  const unsavedRef   = useRef(false);  // flips to true after *any* change
  // ① brand-new (no _id)  ➜ draft
  // ② saved + “incomplete” ➜ draft
  // ③ everything else     ➜ published
  /* A draft is *anything* that is NOT marked “published” or “complete”.
    (Undefined counts as draft; having an _id is irrelevant.)          */
  const isDraft = formState?.post_status !== 'published' &&
                 formState?.post_status !== 'complete';
  const isPublished = !isDraft;

  const [textEditorSelector, setTextEditorSelector] = useState(null);

  // Helper to restore cursor
  function restoreCursor() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      // Save a clone of the first range
      setTextEditorSelector(sel.getRangeAt(0).cloneRange());
    }
  }

  // content and height
  const [containerHeight, setContainerHeight] = useState(1000);

  // inserted elements trackers
  const [hasMediaElement, setHasMediaElement] = useState(false);
  const [hasPollQuizElement, setHasPollQuizElement] = useState(false);

  // modal flags
  const [isPollModalOpen, setIsPollModalOpen] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [isArtworkModalOpen, setIsArtworkModalOpen] = useState(false);

  // image/video upload triggers
  const [triggerImageUpload, setTriggerImageUpload] = useState(null);
  const [triggerVideoUpload, setTriggerVideoUpload] = useState(null);

  // tagging hook
  const {
    isTagging,
    tagQuery,
    showTagPopup,
    setShowTagPopup,
    setIsTagging,
    setTagQuery
  } = useTagging(editableRef);

  // toggle for insert options popup
  const [showInsertOptions, setShowInsertOptions] = useState(false);

  // ——— Handlers ———
  function handleContentInput() {
    if (!editableRef.current) return;
    if (editableRef.current.scrollHeight >= containerHeight - 50) {
      setContainerHeight(h => h + 1000);
    }
  }

  const handlePaste = (event) => {
    if (previewMode) return;
    const clipboard = event.clipboardData;
    if (!clipboard) return;
    const htmlData = clipboard.getData('text/html');
    if (htmlData) {
      event.preventDefault();
      const sanitizedHTML = sanitizePostHtml(htmlData);
      if (sanitizedHTML) {
        document.execCommand('insertHTML', false, sanitizedHTML);
      }
      return;
    }

    const textData = clipboard.getData('text/plain');
    if (textData) {
      event.preventDefault();
      const escapedText = textData.replace(/\n/g, '<br>');
      document.execCommand('insertHTML', false, escapedText);
    }
  };

  // ——— Effects ———
  useEffect(() => {
    if (editableRef?.current) {
      document.execCommand('defaultParagraphSeparator', false, 'div');
    }
  }, []);

  // Handle changes to editable content
  useEffect(() => {
    const handler = e => {
      if (!previewMode && unsavedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [previewMode]);

  // Insert-option handlers
  const openImage   = () => {  if (!postMode || !hasMediaElement) triggerImageUpload?.(); setShowInsertOptions(false); };
  const openVideo   = () => {  if (!postMode || !hasMediaElement) triggerVideoUpload?.(); setShowInsertOptions(false); };
  const openArtwork = () => { if (!postMode || !hasMediaElement) setIsArtworkModalOpen(true); setShowInsertOptions(false); };
  const openPoll    = () => { if (!postMode || !hasPollQuizElement) setIsPollModalOpen(true); setShowInsertOptions(false); };
  const openQuiz    = () => { if (!postMode || !hasPollQuizElement) setIsQuizModalOpen(true); setShowInsertOptions(false); };

  // INIT STRUCTURE we get the date from the formState
  const {
    _id,
    post_structure,
    post_texts,
    post_polls,
    post_quizzes,
    post_pictures,
    post_artworks,
    gallery,
  } = formState;

  // — Sync flags whenever the formState blocks change —
  useEffect(() => {
    if (postMode) {
      // media = pictures OR artworks
      setHasMediaElement(
        post_pictures.length > 0 || post_artworks.length > 0
      );
      // poll/quiz = polls OR quizzes
      setHasPollQuizElement(
        post_polls.length > 0 || post_quizzes.length > 0
      );
    } else {
      // in article mode, no restriction
      setHasMediaElement(false);
      setHasPollQuizElement(false);
    }
  }, [
    postMode,
    post_pictures.length,
    post_artworks.length,
    post_polls.length,
    post_quizzes.length
  ]);

  // 1) Functions to get the corresponding poll,quizz,picture,artwork when we are iterating the structure
  const textsById = useMemo(() =>
    Object.fromEntries(post_texts.map(t => [t.textId, t])),
  [post_texts]);
  const pollsById    = useMemo(() => Object.fromEntries(
    post_polls.map(p => [p.pollId, p])
  ), [post_polls]);
  const quizzesById  = useMemo(() => Object.fromEntries(
    post_quizzes.map(q => [q.quizId, q])),
  [post_quizzes]);
  const picturesById = useMemo(
    () => Object.fromEntries(post_pictures.map(q => [q.groupId, q])),
    [post_pictures]
  );
  const artworksById = useMemo(
    () => Object.fromEntries(post_artworks.map(q => [q.groupId, q])),
    [post_artworks]
  );
  // END Functions

  const [pollToEdit, setPollToEdit] = useState(null);
  const [quizToEdit, setQuizToEdit] = useState(null);

  /* HANDLE REMOVES */
  const handlePollRemove = (id) => {
    setFormState(prev => {
      return {
        ...prev,
        post_polls: prev.post_polls.filter(p => p.pollId !== id),
      };
    });
  };
  const handleQuizRemove = id => {
    setFormState(prev => {
      return {
        ...prev,
        post_quizzes: prev.post_quizzes.filter(p => p.quizId !== id),
      };
    });
  };
  /* END HANDLE REMOVES */

  /* REMOVE ELEMENTS */
  const removeGenericFromDom = (containerId) => {
    const container = document.getElementById(containerId);
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  const removePollFromDom = (pollId) => {
    removeGenericFromDom(`poll-${pollId}`);
    handlePollRemove(pollId);
  };

  const removeQuizFromDom = (quizId) => {
    removeGenericFromDom(`quiz-${quizId}`);
    handleQuizRemove(quizId);
  };

  // Functions for pictures
  const removeImageFromDom = (groupId, pictureId) => {
    // update state: only drop the one specific image
    const id = `groupPicture-${groupId}`;

    // TODO: check when it is removing a picture Id
    setFormState(prev => {
      const groupPictures = prev.post_pictures.find(pic => pic.groupId === groupId);
      if(!pictureId || groupPictures.length === 1) {
        removeGenericFromDom(id);
        return {
          ...prev,
          post_pictures: prev.post_pictures.filter(
            pic => pic.groupId !== groupId
          )
        };
      }

      const filteredPictures = groupPictures.pictures.filter(current => 
        current.pictureId !== pictureId);

      // if we are removing an specific pictureId
      return {
        ...prev,
        post_pictures: prev.post_pictures.map((pic) => {
          const currentPictures = pic.groupId === groupId ? filteredPictures : pic.pictures;
          return {
            ...pic,
            pictures: currentPictures,
          };
        }),
        
      };
    });
  };
  /* END REMOVES */

  /* EDIT CALLBACKS */
  const handlePollEdit = (id, polls) => {
    const poll = polls.find(p => p.pollId === id);
    if (poll) {
      setPollToEdit(poll);
      setIsPollModalOpen(true);
    }
  };

  const handleQuizEdit = (id, quizzes) => {
    const quiz = quizzes.find(x => x.quizId === id);
    if(quiz) {
      setQuizToEdit(quiz);
      setIsQuizModalOpen(true);
    }
  };
  /* END EDITS */

  // now accepts (groupId, slideIndex) and only removes that single slide
  const onArtworkRemove = (groupId, artworkId) => {
    const id = `groupArtwork-${groupId}`;

    setFormState(prev => {
      const groupedArtworks = prev.post_artworks.find(group => group.groupId === groupId);
      if(!artworkId || groupedArtworks.artworks.length === 1) {
        removeGenericFromDom(id);
        return {
          ...prev,
          post_artworks: prev.post_artworks.filter(
            pic => pic.groupId !== groupId
          )
        };
      }

      const filteredArtworks = groupedArtworks.artworks.filter(current => 
        current.artworkId !== artworkId);

      // if we are removing an specific artworkId
      return {
        ...prev,
        post_artworks: prev.post_artworks.map((currentGroupedArtworks) => {
          const currentArtworks = currentGroupedArtworks.groupId === groupId ? filteredArtworks : currentGroupedArtworks.artworks;
          return {
            ...currentGroupedArtworks,
            artworks: currentArtworks,
          };
        }),
        
      };
    });
  };

  const [activeCaption, setActiveCaption] = useState(null);

  // useEffect to handle caption updates
  // Don't come up with other solution
  useEffect(() => {
    if(activeCaption) {
      const { pictureId, caption, groupId } = activeCaption;

      setFormState(prev => ({
        ...prev,
        post_pictures: prev.post_pictures.map(group =>
          group.groupId === groupId
            ? { ...group,
              pictures: group.pictures.map((pic) => 
                pic.pictureId === pictureId ? {
                  ...pic,
                  picture_description: caption,
                }:
                  { ...pic })
            }
            : group
        )
      }));
      setActiveCaption(null);
    }
  }, [activeCaption]);

  // we modify the activeCaption to trigger the useEffect
  const handleCaptionChange = (groupId, pictureId, caption) => {
    // the update is done in the useEffect
    setActiveCaption({ groupId, pictureId, caption });
  };

  // Add your castVote function just below those:
  const castVote = async (pollId, optionIndex) => {
    const url = `${config.apis.api.url}/post/${_id}/polls/${pollId}/vote`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ optionIndex })
    });
  
    const response = await res.json();
  
    // 1) Already voted?
    if (res.status === 403) {
      // update your local state to hide buttons
      setFormState(form => ({
        ...form,
        post_polls: form.post_polls.map(p =>
          p.pollId === pollId ? { ...p, hasVoted: true } : p
        ),
      }));
      return pollsById[pollId].options;
    }
  
    // 2) Any other error?
    if (!res.ok) {
      throw new Error(`Vote failed (${res.status}): ${response}`);
    }
  
    // 3) Now parse JSON safely
    const { poll: updated } = response;
    setFormState(form => ({
      ...form,
      post_polls: form.post_polls.map(p =>
        p.pollId === updated.pollId ? updated : p
      ),
    }));

    return updated.options;
  };

  // ——— NEW: one‐and‐done quiz answer ———
  const castAnswer = async (quizId, answerIndex) => {
    // 1) call the API
    const url = `${config.apis.api.url}/post/${_id}/quizzes/${quizId}/answer`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answerIndex })
    });

    if (res.status === 403) return; // already answered on server
    if (!res.ok) throw new Error(`Answer failed (${res.status})`);

    // 3) also persist into formState so that on a full remount
    //    we still know which one they picked
    setFormState(prev => ({
      ...prev,
      post_quizzes: prev.post_quizzes.map(q =>
        q.quizId === quizId
          ? { ...q, hasAnswered: true, userAnswer: answerIndex } // Updated to include userAnswer
          : q
      )
    }));
  };

  // ——— Set the different types of media we have ———
  const mediaTypes = ['post_pictures', 'post_artworks'];

  const mediaBlocks = postMode
    ? post_structure.filter(b => mediaTypes.includes(b.type))
    : [];

  const [content, setContent] = useState('');

  useEffect(() => {
    const editBodyBlocks = postMode
      ? post_structure.filter(b => !mediaTypes.includes(b.type))
      : post_structure;

    if(editBodyBlocks) {
      let customContent = editBodyBlocks.map((block, idx) => {
        switch (block.type) {
        case 'post_texts': {
          const txt = textsById[block.id];
          if (!txt) return null;
          return (
            <div
              key={idx}
              dangerouslySetInnerHTML={{ __html: txt.content }}
            />
          );
        }

        case 'post_polls': {
          const poll = pollsById[block.id];
          if (!poll) return null;

          return (
            <div
              key={idx}
              id={`poll-${block.id}`}
              data-atomic="true"
              contentEditable="false"
            >
              <PollDisplay
                pollId={poll.pollId}
                title={poll.question}
                options={poll.options}
                initialHasVoted={poll.hasVoted}
                initialUserAnswer={poll.userAnswer}
                previewMode={previewMode}
                onVote={poll.published ? castVote : undefined}
                onEditClick={() => handlePollEdit(poll.pollId, post_polls)}
                hasAnybodyAnswered={poll.hasAnybodyAnswered}
              />
            </div>
          );
        }

        case 'post_quizzes': {
          const quiz = quizzesById[block.id];
          if (!quiz) return null;

          return (
            <div key={idx} id={`quiz-${block.id}`}
              data-atomic="true" 
              contentEditable="false">
              <QuizDisplay
                quizId={quiz.quizId}
                question={quiz.question}
                options={quiz.options}
                correctOption={quiz.correctOption}
                previewMode={previewMode}
                disableSelection={true}
                initialHasAnswered={quiz.hasAnswered}
                userAnswer={quiz.userAnswer}
                onAnswer={quiz.published ? castAnswer : undefined }
                onEditClick={() => handleQuizEdit(quiz.quizId, post_quizzes)}
                hasAnybodyAnswered={quiz.hasAnybodyAnswered}
              />
            </div>
          );
        }

        case 'post_pictures': {
          const pics = picturesById[block.id] || [];

          return (
            <div
              key={idx}
              id={`groupPicture-${block.id}`}
              data-atomic="true"
              contentEditable="false"
            >
              <ImageDisplay 
                groupId={pics.groupId}
                pictures={pics.pictures}
                previewMode={previewMode}
                handleImageRemove={removeImageFromDom}
                handleCaptionChange={handleCaptionChange}
              />
            </div>
          );
        }

        case 'post_artworks': {
          const groupedArtworks = artworksById[block.id] || [];
          return (
            <div
              key={idx}
              id={`groupArtwork-${block.id}`}
              data-atomic="true"
              contentEditable="false"
            >
              <ArtworkDisplay
                groupedArtworks={groupedArtworks}
                onArtworkRemove={onArtworkRemove}
                previewMode={previewMode}
                navigate={navigate}
                location={location}
              />
            </div>);
        }
        default:
          return null;
        }
      });
      if (
        !previewMode &&
        editBodyBlocks.length > 0 &&
        editBodyBlocks[0].type !== 'post_texts'
      ) {
        // Ensure there's an editable spacer before non-text blocks so users can prepend copy.
        customContent.unshift(
          <div
            key="top-spacer"
            className="add-post__article-top-spacer"
            data-placeholder-block="true"
            contentEditable
            suppressContentEditableWarning
          >
            <br />
          </div>
        );
      }
      // we ensure we have a final line break
      customContent.push(<span key="to-type" id='to-type'><br/></span>);
      setContent(customContent);
    } else {
      // is new article
      // TODO: Check if I have to do something
    }

  }, []);

  return (
    <div>
      {postMode && mediaBlocks.length > 0 && (
        <div className="add-post__media-top-element">
          {mediaBlocks.map((block, idx) => {
            switch (block.type) {
            case 'post_pictures': {
              const pics = picturesById[block.id] || [];

              return (
                <div
                  key={idx}
                  id={`groupPicture-${block.id}`}
                  data-atomic="true"
                  contentEditable="false"
                >
                  <ImageDisplay 
                    groupId={pics.groupId}
                    pictures={pics.pictures}
                    previewMode={previewMode}
                    handleImageRemove={removeImageFromDom}
                    handleCaptionChange={handleCaptionChange}
                  />
                </div>
              );
            }

            case 'post_artworks': {
              const groupedArtworks = artworksById[block.id] || [];

              return (
                <div
                  key={idx}
                  id={`groupArtwork-${block.id}`}
                  data-atomic="true"
                  contentEditable="false"
                >
                  <ArtworkDisplay
                    groupedArtworks={groupedArtworks}
                    onArtworkRemove={onArtworkRemove}
                    previewMode={previewMode}
                    navigate={navigate}
                    location={location}
                  />
                </div>);
            }
              
            default:
              return null;
            }
          })}
        </div>
      )}

      {/* ——— Main editable body ——— */}
      <div
        className={`add-post__article-body${previewMode ? " preview" : ""}`}
        contentEditable={!previewMode}
        ref={editableRef}
        onPaste={handlePaste}
        onInput={(event) => {
          if(!previewMode) {
            unsavedRef.current = true; 
            // to verify we have text content, 
            // otherwise we cannot save it
            setFormState(prevFormState => ({
              ...prevFormState,
              plainText: event.target.innerText
            }));
            // Drop the placeholder marker when the user adds actual text.
            event.currentTarget
              .querySelectorAll('[data-placeholder-block="true"]')
              .forEach(node => {
                if (node.textContent.trim()) {
                  node.removeAttribute('data-placeholder-block');
                }
              });
          }
        }}
        style={{ minHeight: `${containerHeight}px` }}
      >
        {content}
      </div>

      {!previewMode &&
        <PollManager 
          formState={formState} 
          setFormState={setFormState}
          editableRef={editableRef}
          isPollModalOpen={isPollModalOpen}
          setIsPollModalOpen={setIsPollModalOpen}
          handlePollEdit={handlePollEdit}
          removePollFromDom={removePollFromDom}
          setPollToEdit={setPollToEdit}
          pollToEdit={pollToEdit}
          textEditorSelector={textEditorSelector}
        />
      }
      
      {!previewMode && 
        <QuizManager 
          formState={formState} 
          setFormState={setFormState}
          editableRef={editableRef}
          isQuizModalOpen={isQuizModalOpen}
          setIsQuizModalOpen={setIsQuizModalOpen}
          handleQuizEdit={handleQuizEdit}
          setQuizToEdit={setQuizToEdit}
          quizToEdit={quizToEdit}
          textEditorSelector={textEditorSelector}
          removeQuizFromDom={removeQuizFromDom}/>
      }

      {!previewMode && 
          <ArtworkManager 
            open={isArtworkModalOpen}
            close={() => setIsArtworkModalOpen(false)}
            formState={formState}
            setFormState={setFormState}
            editableRef={editableRef}
            textEditorSelector={textEditorSelector}
            handleArtworkRemove={onArtworkRemove}
            postMode={postMode} />
      }

      {!previewMode && (
        <ImageManager
          formState={formState}
          setFormState={setFormState}
          editableRef={editableRef}
          textEditorSelector={textEditorSelector}
          setTriggerImageUpload={setTriggerImageUpload}
          previewMode={previewMode}
          handleImageRemove={removeImageFromDom}
          handleCaptionChange={handleCaptionChange}
          postMode={postMode}
        />
      )}

      <TextFormattingToolbar editableRef={editableRef} previewMode={previewMode} postMode={postMode} />
      
      {showTagPopup && (
        <TagPopup open={showTagPopup} 
          close={() => { setShowTagPopup(false); setIsTagging(false); setTagQuery(''); }} 
          addTag={() => {}} removeTag={() => {}} formState={formState} contentEditableRef={editableRef} setFormState={setFormState} />
      )}

      {/* <BoardBodyVideo formState={formState} setFormState={setFormState} editableRef={editableRef} setTriggerVideoUpload={setTriggerVideoUpload} postMode={postMode} /> */}

      {!hideAddButton && (
        <ProfileAddButton
          iconOnly
          alignRight
          ariaLabel="Add Content"
          onClick={() => {
            restoreCursor();
            setShowInsertOptions(true);
          }}
        />
      )}
      
      <GenericPopup open={showInsertOptions} darkerBlur className="remove-scrollbar board-text-body__insert-options-popup">
        <div className="pop-up-artwork-create" onClick={() => setShowInsertOptions(false)}>
          <div className="pop-up-artwork-create-container" onClick={e => e.stopPropagation()}>
            {(!postMode || !hasMediaElement) && (
              <div className="pop-up-artwork-create-options" onClick={openImage}><Text>Image</Text></div>
            )}
            {(!postMode || !hasMediaElement) && (
              <div className="pop-up-artwork-create-options" onClick={openVideo}><Text>Video</Text></div>
            )}
            {(!postMode || !hasMediaElement) && (
              <div className="pop-up-artwork-create-options" onClick={openArtwork}><Text>Artwork</Text></div>
            )}
            {(!postMode || !hasPollQuizElement) && (
              <div className="pop-up-artwork-create-options" onClick={openPoll}><Text>Poll</Text></div>
            )}
            {(!postMode || !hasPollQuizElement) && (
              <div className="pop-up-artwork-create-options" onClick={openQuiz}><Text>Quiz</Text></div>
            )}
          </div>
        </div>
      </GenericPopup>
    </div>
  );
}
